// frontend/src/pages/TradeSmart.jsx
import React from "react";
import { motion } from "framer-motion";

export default function TradeSmart() {
  return (
    <motion.div
      className="min-h-screen bg-blue-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 pb-12 md:pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur shadow-md border-b-4 border-blue-600 dark:border-blue-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-16 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 dark:text-blue-300 mb-3 md:mb-4">
            Trade Smart
          </h1>
          <p className="text-blue-700/90 dark:text-blue-200/90 text-sm sm:text-lg md:text-xl max-w-2xl mx-auto">
            Master the mindset and methods that help you trade profitably and safely in crypto markets.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 md:mt-16 space-y-12 md:space-y-20">
        {/* Smart Trading Principles */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-4 md:mb-6">
            Smart Trading Principles
          </h2>
          <ul className="space-y-3 sm:space-y-4">
            {[
              {
                icon: "📈",
                title: "Plan Before You Trade:",
                body:
                  "Set a clear goal before every trade. Are you scalping, holding long-term, or taking profits? Define your entry, target, and stop-loss in advance.",
              },
              {
                icon: "🔒",
                title: "Use Stop-Loss & Take-Profit:",
                body:
                  "Never enter a trade without knowing when to exit. Protect your downside and lock in profits automatically.",
              },
              {
                icon: "📊",
                title: "Watch Volume & Trends:",
                body:
                  "Don’t just follow price—check trading volume, momentum, and support/resistance levels to confirm market direction.",
              },
              {
                icon: "🧠",
                title: "Control Emotions:",
                body:
                  "Avoid revenge trading after losses or FOMO buying during spikes. Follow logic, not hype.",
              },
              {
                icon: "💼",
                title: "Diversify Your Portfolio:",
                body:
                  "Don’t go all-in on one coin. Spread risk across assets and avoid betting your entire balance on a single position.",
              },
            ].map((item) => (
              <li
                key={item.title}
                className="flex items-start rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 sm:p-5 shadow-sm text-slate-700 dark:text-slate-300"
              >
                <span className="mr-3 sm:mr-4 text-blue-600 dark:text-blue-300 text-2xl sm:text-3xl">
                  {item.icon}
                </span>
                <div className="text-sm sm:text-base">
                  <strong className="text-slate-900 dark:text-slate-100">{item.title}</strong>
                  <div className="mt-0.5">{item.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Risk Management Tips */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-4 md:mb-6">
            Risk Management Tips
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 text-slate-700 dark:text-slate-300">
            {[
              {
                title: "Risk 1–2% Per Trade",
                body:
                  "Never risk more than 2% of your capital on a single trade. This keeps you alive even with losing streaks.",
              },
              {
                title: "Avoid Leverage as a Beginner",
                body:
                  "Leverage multiplies both profits and losses. Use it only when you fully understand liquidation and margin.",
              },
              {
                title: "Use Limit Orders",
                body:
                  "Set your price with limit orders instead of chasing price using market orders—especially in volatile markets.",
              },
              {
                title: "Track Every Trade",
                body:
                  "Maintain a journal of your entries, exits, reasoning, and outcomes to learn and refine your edge over time.",
              },
            ].map((card) => (
              <motion.div
                key={card.title}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 250, damping: 20 }}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 sm:p-5 md:p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-base sm:text-xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5">
                  {card.title}
                </h3>
                <p className="text-sm sm:text-base">{card.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Trade Smart With Coinzetto */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-4 md:mb-6">
            How Coinzetto Helps You Trade Smart
          </h2>
          <ul className="list-disc list-inside text-sm sm:text-base text-slate-700 dark:text-slate-300 space-y-2 sm:space-y-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4 sm:p-5 md:p-6 shadow-sm">
            <li>Low fees with clear tax breakdown (GST + TDS) shown before order confirmation.</li>
            <li>Real-time charts, order books, and volume data to support technical decision making.</li>
            <li>24/7 support and educational guides to help you grow as a trader.</li>
            <li>Integrated KYC and safety controls to protect your assets and access.</li>
          </ul>
        </section>

        {/* CTA */}
        <section className="text-center pt-2 md:pt-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-2 md:mb-4">
            Start Trading Smart
          </h2>
          <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 mb-4 md:mb-6 max-w-xl mx-auto">
            Create your Coinzetto account today and trade smarter with confidence, safety, and strategy.
          </p>
          <a
            href="/signup"
            className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:focus-visible:ring-blue-500 shadow"
          >
            Open Account
          </a>
        </section>
      </main>
    </motion.div>
  );
}
